import React, { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { STATUS_META } from "@/lib/status";
import {
  ClipboardList, FolderOpen, Loader, CheckCircle2, XCircle, Star, Wrench, Trophy, AlertTriangle,
} from "lucide-react";

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <Card className="border-slate-200" data-testid={testid}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-800 leading-none">{value}</p>
          <p className="text-xs text-slate-500 mt-1">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function Stars({ value }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} className={`h-3.5 w-3.5 ${value >= n ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

export default function MetricsPanel() {
  const [m, setM] = useState(null);

  useEffect(() => {
    api.get("/metrics").then(({ data }) => setM(data)).catch(() => setM(null));
  }, []);

  if (!m) return <p className="text-slate-400 text-sm">Carregando métricas...</p>;

  const statusOrder = ["aberto", "atribuido", "aceito", "a_caminho", "concluido", "cancelado"];
  const maxStatus = Math.max(1, ...statusOrder.map((s) => m.by_status[s] || 0));

  return (
    <div className="space-y-6" data-testid="metrics-panel">
      {/* Cards principais */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <StatCard icon={ClipboardList} label="Total" value={m.total} color="bg-slate-100 text-slate-600" testid="metric-total" />
        <StatCard icon={FolderOpen} label="Abertos" value={m.abertos} color="bg-amber-100 text-amber-700" testid="metric-abertos" />
        <StatCard icon={Loader} label="Em andamento" value={m.em_andamento} color="bg-purple-100 text-purple-700" testid="metric-andamento" />
        <StatCard icon={CheckCircle2} label="Concluídos" value={m.concluidos} color="bg-green-100 text-green-700" testid="metric-concluidos" />
        <StatCard icon={AlertTriangle} label="Alertas SLA (24h)" value={m.sla_alerts ?? 0} color="bg-red-100 text-red-700" testid="metric-sla" />
        <StatCard icon={Star} label={`Nota média (${m.ratings_count})`} value={m.avg_rating ?? "—"} color="bg-amber-100 text-amber-700" testid="metric-nota" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Distribuição por status */}
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-800 mb-4">Chamados por status</h3>
            <div className="space-y-3">
              {statusOrder.map((s) => {
                const val = m.by_status[s] || 0;
                const pct = (val / maxStatus) * 100;
                return (
                  <div key={s} data-testid={`status-bar-${s}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">{STATUS_META[s]?.label || s}</span>
                      <span className="font-medium text-slate-700">{val}</span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#0a2a6c] to-[#1e7be0] rounded-full transition-all"
                        style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Ranking de técnicos */}
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" /> Desempenho por técnico
            </h3>
            {m.technicians.length === 0 ? (
              <p className="text-sm text-slate-400">Nenhum técnico cadastrado.</p>
            ) : (
              <div className="space-y-3">
                {m.technicians.map((t) => (
                  <div key={t.id} className="flex items-center gap-3" data-testid={`tech-metric-${t.id}`}>
                    <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Wrench className="h-4 w-4 text-slate-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{t.name}</p>
                      <p className="text-xs text-slate-500">
                        {t.concluidos} concluídos · {t.em_andamento} em andamento · {t.assigned} no total
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {t.avg_rating != null ? (
                        <>
                          <Stars value={t.avg_rating} />
                          <p className="text-[11px] text-slate-400 mt-0.5">{t.avg_rating} ({t.ratings_count})</p>
                        </>
                      ) : (
                        <span className="text-xs text-slate-300">sem avaliações</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
